import { useEffect, useState, Fragment } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Trash, Check, ArrowSquareOut, Warning } from "@phosphor-icons/react";

const REASON_LABEL = {
  virus: "Virus / malware",
  frame_breaker: "Frame breaker",
  scam: "Scam / phishing",
  adult: "Adult / NSFW",
  broken: "Broken / 404",
  other: "Other",
};

const REASON_TONE = {
  virus: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
  frame_breaker: "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300",
  scam: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  adult: "bg-pink-100 text-pink-800 dark:bg-pink-500/15 dark:text-pink-300",
  broken: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  other: "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-200",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/website-reports?status=${status}`);
      setReports(data?.reports || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const deleteWebsite = async (r) => {
    if (!r.website_id) return;
    if (!window.confirm(`Delete website "${r.title || r.url}" for good?`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/websites/${r.website_id}`);
      await api.post(`/admin/website-reports/${r.id}/resolve`);
      toast.success("Website deleted and report resolved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId("");
    }
  };

  const resolve = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/website-reports/${r.id}/resolve`);
      toast.success("Report resolved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId("");
    }
  };

  const dismiss = async (r) => {
    if (!window.confirm("Dismiss this report? The record will be removed.")) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/website-reports/${r.id}`);
      toast.success("Report dismissed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId("");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Reported websites
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Members flag viruses, frame breakers and scam sites here.</p>
        </div>
        <div className="flex gap-2">
          {[
            { v: "open", label: "Open" },
            { v: "resolved", label: "Resolved" },
            { v: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`reports-tab-${t.v}`}
              onClick={() => setStatus(t.v)}
              className={`rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-widest border transition ${
                status === t.v
                  ? "bg-yellow-400 text-yellow-950 border-yellow-500"
                  : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-yellow-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left py-3 px-4">Website</th>
              <th className="text-left py-3 px-4">Reason</th>
              <th className="text-left py-3 px-4">Reporter</th>
              <th className="text-left py-3 px-4">When</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && reports.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-slate-500 flex flex-col items-center gap-2"><Flag size={20} weight="duotone" /> No reports in this bucket.</td></tr>
            )}
            {!loading && reports.map((r) => {
              const gone = !r.website;
              return (
                <Fragment key={r.id}>
                  <tr data-testid={`report-row-${r.id}`} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-3 px-4 align-top">
                      <div className="font-semibold text-slate-900 dark:text-white truncate max-w-[280px]">{r.title || r.url}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 max-w-[280px]">
                        <a href={r.url} target="_blank" rel="noreferrer" className="truncate hover:underline">{r.url}</a>
                        <ArrowSquareOut size={11} weight="bold" className="shrink-0 opacity-60" />
                      </div>
                      {gone && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-rose-600 dark:text-rose-400"><Warning size={11} weight="bold" /> already deleted</div>
                      )}
                      {r.note && (
                        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 italic max-w-[320px]">“{r.note}”</div>
                      )}
                    </td>
                    <td className="py-3 px-4 align-top">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${REASON_TONE[r.reason] || REASON_TONE.other}`}>
                        {REASON_LABEL[r.reason] || r.reason}
                      </span>
                    </td>
                    <td className="py-3 px-4 align-top text-slate-700 dark:text-slate-200">@{r.reporter_username || "?"}</td>
                    <td className="py-3 px-4 align-top text-slate-500 dark:text-slate-400">{fmt(r.created_at)}</td>
                    <td className="py-3 px-4 align-top">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {r.status === "open" && !gone && (
                          <button
                            data-testid={`report-delete-site-${r.id}`}
                            onClick={() => deleteWebsite(r)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-3 py-1.5 disabled:opacity-60"
                          >
                            <Trash size={12} weight="bold" /> Delete site
                          </button>
                        )}
                        {r.status === "open" && (
                          <button
                            data-testid={`report-resolve-${r.id}`}
                            onClick={() => resolve(r)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 rounded-full bg-lime-500 hover:bg-lime-600 text-slate-900 text-xs font-bold px-3 py-1.5 disabled:opacity-60"
                          >
                            <Check size={12} weight="bold" /> Resolve
                          </button>
                        )}
                        <button
                          data-testid={`report-dismiss-${r.id}`}
                          onClick={() => dismiss(r)}
                          disabled={busyId === r.id}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold px-3 py-1.5 disabled:opacity-60"
                        >
                          <Trash size={12} /> Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
